/**
 * Pure helpers for the Metalwood landing attribution contract.
 * This file intentionally has no DOM, storage, network, or consent side effects.
 */

((root, factory) => {
  root.StarmistAttributionCore = factory();
})(typeof window === 'undefined' ? this : window, () => {
  'use strict';

  const SCHEMA_VERSION = 1;
  const EVENT_SCHEMA_VERSION = 1;
  const AD_TOUCH_SCHEMA_VERSION = 1;
  const ATTRIBUTION_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;
  const FUTURE_CLOCK_SKEW_MS = 5 * 60 * 1000;

  const ORIGIN_ATTRIBUTE_KEYS = Object.freeze([
    'sm_attr_schema',
    'sm_journey_id',
    'sm_first_landing_key',
    'sm_first_landing_version',
    'sm_first_landing_path',
    'sm_first_landing_at',
    'sm_utm_source',
    'sm_utm_medium',
    'sm_utm_campaign',
    'sm_utm_content',
    'sm_utm_term',
    'sm_utm_id',
    'sm_meta_campaign_id',
    'sm_meta_campaign_name',
    'sm_meta_adset_id',
    'sm_meta_adset_name',
    'sm_meta_ad_id',
    'sm_meta_ad_name',
    'sm_fbclid',
    'sm_shopify_region',
    'sm_shopify_country',
  ]);

  const JOURNEY_ATTRIBUTE_KEYS = Object.freeze([
    'sm_selected_format',
    'sm_upsell_key',
    'sm_upsell_version',
    'sm_upsell_decision',
  ]);

  // Additive only: the original first-touch and journey fields retain their meaning.
  // Names and click IDs stay in individual analytics events, not the cart URL.
  const TOUCH_ATTRIBUTE_KEYS = Object.freeze([
    'sm_touch_schema',
    'sm_last_touch_id',
    'sm_last_touch_at',
    'sm_last_landing_key',
    'sm_last_landing_version',
    'sm_last_landing_path',
    'sm_last_utm_source',
    'sm_last_utm_medium',
    'sm_last_utm_campaign',
    'sm_last_utm_content',
    'sm_last_utm_term',
    'sm_last_utm_id',
    'sm_last_meta_campaign_id',
    'sm_last_meta_adset_id',
    'sm_last_meta_ad_id',
    'sm_last_shopify_region',
    'sm_last_shopify_country',
  ]);

  const OWNED_ATTRIBUTE_KEYS = Object.freeze([
    ...ORIGIN_ATTRIBUTE_KEYS, ...JOURNEY_ATTRIBUTE_KEYS, ...TOUCH_ATTRIBUTE_KEYS,
  ]);

  const PARAMETER_SPEC = Object.freeze({
    utm_source: { attribute: 'sm_utm_source', maxLength: 80, kind: 'text' },
    utm_medium: { attribute: 'sm_utm_medium', maxLength: 80, kind: 'text' },
    utm_campaign: { attribute: 'sm_utm_campaign', maxLength: 160, kind: 'text' },
    utm_content: { attribute: 'sm_utm_content', maxLength: 160, kind: 'text' },
    utm_term: { attribute: 'sm_utm_term', maxLength: 160, kind: 'text' },
    utm_id: { attribute: 'sm_utm_id', maxLength: 100, kind: 'token' },
    meta_campaign_id: { attribute: 'sm_meta_campaign_id', maxLength: 32, kind: 'meta-id' },
    meta_campaign_name: { attribute: 'sm_meta_campaign_name', maxLength: 160, kind: 'text' },
    meta_adset_id: { attribute: 'sm_meta_adset_id', maxLength: 32, kind: 'meta-id' },
    meta_adset_name: { attribute: 'sm_meta_adset_name', maxLength: 160, kind: 'text' },
    meta_ad_id: { attribute: 'sm_meta_ad_id', maxLength: 32, kind: 'meta-id' },
    meta_ad_name: { attribute: 'sm_meta_ad_name', maxLength: 160, kind: 'text' },
    fbclid: { attribute: 'sm_fbclid', maxLength: 240, kind: 'click-id' },
  });

  const PROFILE_KEY_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  const VERSION_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*:[a-z0-9]+(?:-[a-z0-9]+)*-r[1-9][0-9]{0,2}$/;
  const TOKEN_PATTERN = /^[A-Za-z0-9._~-]+$/;
  const META_ID_PATTERN = /^[0-9]{1,32}$/;
  const CLICK_ID_PATTERN = /^[A-Za-z0-9._~-]+$/;
  const JOURNEY_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const SHOPIFY_REGION_PATTERN = /^[A-Z]{2}(?:[A-Z0-9]{1,3})?$/;
  const SHOPIFY_COUNTRY_PATTERN = /^[A-Z]{2}$/;
  const PAGE_PATH_PATTERN = /^\/pages\/[a-z0-9]+(?:-[a-z0-9]+)*$/;
  const PATH_PATTERN = /^\/(?:pages\/[a-z0-9]+(?:-[a-z0-9]+)*|products\/(?=[a-z0-9-]{1,255}$)[a-z0-9]+(?:-[a-z0-9]+)*)$/;
  const VARIANT_ID_PATTERN = /^[0-9]{1,24}$/;
  const DISCOUNT_PATTERN = /^[A-Za-z0-9_-]{1,80}$/;
  const CTA_TOKEN_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,79}$/;
  const ALLOWED_FORMATS = Object.freeze(['ebook', 'paperback', 'hardcover', 'audio']);
  const ALLOWED_DECISIONS = Object.freeze(['accepted', 'declined']);
  const ALLOWED_THEME_ROLES = Object.freeze(['main', 'unpublished', 'development', 'demo']);

  const stripControlCharacters = (value) => value.replace(/[\u0000-\u001f\u007f-\u009f]/g, '');

  const sanitizeText = (value, maxLength) => {
    if (typeof value !== 'string') return '';
    return stripControlCharacters(value).trim().slice(0, maxLength);
  };

  const sanitizeToken = (value, maxLength, pattern = TOKEN_PATTERN) => {
    const sanitized = sanitizeText(value, maxLength);
    return pattern.test(sanitized) ? sanitized : '';
  };

  const sanitizeParameter = (value, spec) => {
    switch (spec.kind) {
      case 'meta-id':
        return sanitizeToken(value, spec.maxLength, META_ID_PATTERN);
      case 'click-id':
        return sanitizeToken(value, spec.maxLength, CLICK_ID_PATTERN);
      case 'token':
        return sanitizeToken(value, spec.maxLength);
      default:
        return sanitizeText(value, spec.maxLength);
    }
  };

  const normalizeShopifyRegion = (value) => {
    const region = sanitizeText(typeof value === 'string' ? value.toUpperCase() : '', 5);
    if (!SHOPIFY_REGION_PATTERN.test(region)) {
      return Object.freeze({ region: '', country: '' });
    }

    return Object.freeze({ region, country: region.slice(0, 2) });
  };

  const normalizeAttributionParameters = (search) => {
    const normalized = {};
    const parameters = new URLSearchParams(typeof search === 'string' ? search : '');

    Object.entries(PARAMETER_SPEC).forEach(([parameter, spec]) => {
      const firstValidValue = parameters
        .getAll(parameter)
        .map((value) => sanitizeParameter(value, spec))
        .find(Boolean);

      if (firstValidValue) normalized[spec.attribute] = firstValidValue;
    });

    return normalized;
  };

  const normalizeTrustedProfile = (profile) => {
    if (!profile || typeof profile !== 'object') return null;

    const kind = profile.kind === 'landing' || profile.kind === 'upsell' ? profile.kind : '';
    const profileKey = sanitizeToken(profile.profileKey, 80, PROFILE_KEY_PATTERN);
    const pageVersion = sanitizeToken(profile.pageVersion, 180, VERSION_PATTERN);
    const path = sanitizeToken(profile.path, 300, PATH_PATTERN);
    const revision = Number(profile.profileRevision);
    const templateRelease = sanitizeToken(profile.templateRelease, 80, PROFILE_KEY_PATTERN);
    const attributionLane = sanitizeToken(profile.attributionLane, 80, PROFILE_KEY_PATTERN);
    const candidateThemeRole = sanitizeToken(profile.themeRole, 20, /^[a-z]+$/);
    const themeRole = ALLOWED_THEME_ROLES.includes(candidateThemeRole) ? candidateThemeRole : 'unknown';

    if (
      Number(profile.schema) !== SCHEMA_VERSION ||
      !kind ||
      !profileKey ||
      !pageVersion ||
      !path ||
      !templateRelease ||
      !Number.isInteger(revision) ||
      revision < 1 ||
      revision > 999
    ) {
      return null;
    }

    const productSurface = path.startsWith('/products/');
    const productId = sanitizeToken(profile.productId, 24, VARIANT_ID_PATTERN);
    const productTitle = sanitizeText(profile.productTitle, 200);
    if (productSurface && (
      kind !== 'landing' || profile.surface !== 'product' || !productId || !productTitle ||
      profileKey !== `product-${productId}` || attributionLane !== profileKey ||
      pageVersion !== `${templateRelease}:${profileKey}-r${revision}`
    )) return null;
    if (!productSurface && profile.surface === 'product') return null;

    return Object.freeze({
      schema: SCHEMA_VERSION,
      kind,
      profileKey,
      profileRevision: revision,
      templateRelease,
      pageVersion,
      path,
      attributionLane,
      themeRole,
      ...(productSurface ? { surface: 'product', productId, productTitle } : {}),
    });
  };

  const createFirstTouchRecord = (
    trustedProfile,
    search,
    now = Date.now(),
    journeyId = '',
    shopifyRegion = '',
  ) => {
    const profile = normalizeTrustedProfile(trustedProfile);
    if (!profile || profile.kind !== 'landing' || !Number.isFinite(now)) return null;

    const capturedAt = new Date(now).toISOString();
    const normalizedJourneyId = sanitizeToken(journeyId, 36, JOURNEY_ID_PATTERN);
    const location = normalizeShopifyRegion(shopifyRegion);
    const attributes = {
      sm_attr_schema: String(SCHEMA_VERSION),
      ...(normalizedJourneyId ? { sm_journey_id: normalizedJourneyId } : {}),
      sm_first_landing_key: profile.profileKey,
      sm_first_landing_version: profile.pageVersion,
      sm_first_landing_path: profile.path,
      sm_first_landing_at: capturedAt,
      ...(location.region
        ? {
            sm_shopify_region: location.region,
            sm_shopify_country: location.country,
          }
        : {}),
      ...normalizeAttributionParameters(search),
    };

    return Object.freeze({
      schema: SCHEMA_VERSION,
      capturedAt,
      expiresAt: new Date(now + ATTRIBUTION_WINDOW_MS).toISOString(),
      attributes: Object.freeze(attributes),
    });
  };

  const normalizeOriginAttributes = (attributes) => {
    if (!attributes || typeof attributes !== 'object') return null;

    const normalized = {
      sm_attr_schema: sanitizeToken(String(attributes.sm_attr_schema || ''), 3, /^1$/),
      sm_first_landing_key: sanitizeToken(attributes.sm_first_landing_key, 80, PROFILE_KEY_PATTERN),
      sm_first_landing_version: sanitizeToken(attributes.sm_first_landing_version, 180, VERSION_PATTERN),
      sm_first_landing_path: sanitizeToken(attributes.sm_first_landing_path, 300, PATH_PATTERN),
      sm_first_landing_at: sanitizeText(attributes.sm_first_landing_at, 32),
    };

    const journeyId = sanitizeToken(attributes.sm_journey_id, 36, JOURNEY_ID_PATTERN);
    if (journeyId) normalized.sm_journey_id = journeyId;

    const location = normalizeShopifyRegion(attributes.sm_shopify_region);
    const country = sanitizeToken(attributes.sm_shopify_country, 2, SHOPIFY_COUNTRY_PATTERN);
    if (location.region && country === location.country) {
      normalized.sm_shopify_region = location.region;
      normalized.sm_shopify_country = country;
    }

    Object.values(normalized).forEach((value) => {
      if (!value) normalized.invalid = true;
    });

    Object.values(PARAMETER_SPEC).forEach((spec) => {
      const value = sanitizeParameter(attributes[spec.attribute], spec);
      if (value) normalized[spec.attribute] = value;
    });

    if (normalized.invalid) return null;
    return normalized;
  };

  const normalizeFirstTouchRecord = (record, now = Date.now()) => {
    if (!record || typeof record !== 'object' || Number(record.schema) !== SCHEMA_VERSION) return null;

    const attributes = normalizeOriginAttributes(record.attributes);
    if (!attributes) return null;

    const capturedMs = Date.parse(attributes.sm_first_landing_at);
    if (
      !Number.isFinite(capturedMs) ||
      capturedMs > now + FUTURE_CLOCK_SKEW_MS ||
      now - capturedMs >= ATTRIBUTION_WINDOW_MS
    ) {
      return null;
    }

    delete attributes.invalid;

    return Object.freeze({
      schema: SCHEMA_VERSION,
      capturedAt: new Date(capturedMs).toISOString(),
      expiresAt: new Date(capturedMs + ATTRIBUTION_WINDOW_MS).toISOString(),
      attributes: Object.freeze(attributes),
    });
  };

  const ensureFirstTouchJourneyId = (record, journeyId, now = Date.now()) => {
    const normalizedRecord = normalizeFirstTouchRecord(record, now);
    if (!normalizedRecord) return null;
    if (normalizedRecord.attributes.sm_journey_id) return normalizedRecord;

    const normalizedJourneyId = sanitizeToken(journeyId, 36, JOURNEY_ID_PATTERN);
    if (!normalizedJourneyId) return normalizedRecord;

    return normalizeFirstTouchRecord(
      {
        schema: SCHEMA_VERSION,
        attributes: {
          ...normalizedRecord.attributes,
          sm_journey_id: normalizedJourneyId,
        },
      },
      now,
    );
  };

  const firstTouchFromCartAttributes = (attributes, now = Date.now()) => {
    const normalized = normalizeOriginAttributes(attributes);
    if (!normalized) return null;

    delete normalized.invalid;

    return normalizeFirstTouchRecord(
      {
        schema: SCHEMA_VERSION,
        attributes: normalized,
      },
      now,
    );
  };

  const chooseFirstTouch = ({ cartAttributes, storedRecord, candidateRecord, now = Date.now() }) => {
    const candidates = [
      ['cart', firstTouchFromCartAttributes(cartAttributes, now)],
      ['storage', normalizeFirstTouchRecord(storedRecord, now)],
      ['current-landing', normalizeFirstTouchRecord(candidateRecord, now)],
    ];

    const selected = candidates.find(([, record]) => record);
    return selected ? Object.freeze({ source: selected[0], record: selected[1] }) : Object.freeze({ source: 'none', record: null });
  };

  // A cart contains a purchase snapshot, not the original full arrival profile.
  // Recover only the profile facts actually encoded there; never invent product
  // titles, the historical theme role, or missing ad names for analytics events.
  const normalizeCartTouchProfile = (profile) => {
    if (!profile || typeof profile !== 'object' || profile.kind !== 'landing') return null;
    const profileKey = sanitizeToken(profile.profileKey, 80, PROFILE_KEY_PATTERN);
    const pageVersion = sanitizeToken(profile.pageVersion, 180, VERSION_PATTERN);
    const path = sanitizeToken(profile.path, 300, PATH_PATTERN);
    const versionParts = pageVersion.match(/^([^:]+):(.+)-r([1-9][0-9]{0,2})$/);
    if (!profileKey || !path || !versionParts || versionParts[2] !== profileKey) return null;

    const productSurface = path.startsWith('/products/');
    const productId = productSurface ? profileKey.match(/^product-([0-9]{1,24})$/)?.[1] : '';
    if (productSurface && !productId) return null;
    return Object.freeze({
      schema: SCHEMA_VERSION,
      kind: 'landing',
      profileKey,
      profileRevision: Number(versionParts[3]),
      templateRelease: versionParts[1],
      pageVersion,
      path,
      attributionLane: '',
      themeRole: 'unknown',
      ...(productSurface ? { surface: 'product', productId } : {}),
    });
  };

  const normalizeAdTouch = (touch, now = Date.now()) => {
    if (!touch || typeof touch !== 'object' || Number(touch.schema) !== AD_TOUCH_SCHEMA_VERSION ||
      !Number.isFinite(now)) return null;
    const touchId = sanitizeToken(touch.touchId, 36, JOURNEY_ID_PATTERN);
    const journeyId = sanitizeToken(touch.journeyId, 36, JOURNEY_ID_PATTERN);
    const observedMs = typeof touch.observedAt === 'string' ? Date.parse(touch.observedAt) : NaN;
    const reconstructed = touch.reconstructed === true;
    const profile = reconstructed
      ? normalizeCartTouchProfile(touch.profile)
      : normalizeTrustedProfile(touch.profile);
    if (!touchId || !journeyId || !profile || profile.kind !== 'landing' ||
      !Number.isFinite(observedMs) || observedMs > now + FUTURE_CLOCK_SKEW_MS ||
      now - observedMs >= ATTRIBUTION_WINDOW_MS) return null;

    const parameters = {};
    Object.values(PARAMETER_SPEC).forEach((spec) => {
      const value = sanitizeParameter(touch.parameters?.[spec.attribute], spec);
      if (value) parameters[spec.attribute] = value;
    });
    // A full arrival must contain a recognized attribution parameter. A valid
    // cart snapshot can be empty here when its only parameter was omitted from
    // cart serialization (for example fbclid or an ad name).
    if (!reconstructed && !Object.keys(parameters).length) return null;
    return Object.freeze({
      schema: AD_TOUCH_SCHEMA_VERSION,
      touchId,
      observedAt: new Date(observedMs).toISOString(),
      journeyId,
      profile,
      parameters: Object.freeze(parameters),
      region: normalizeShopifyRegion(touch.region).region,
      ...(reconstructed ? { reconstructed: true } : {}),
    });
  };

  const createAdTouch = (
    trustedProfile, search, journeyId, touchId, shopifyRegion = '', now = Date.now(),
  ) => {
    if (!Number.isFinite(now)) return null;
    const observedAt = new Date(now);
    if (!Number.isFinite(observedAt.getTime())) return null;
    return normalizeAdTouch({
      schema: AD_TOUCH_SCHEMA_VERSION,
      touchId,
      observedAt: observedAt.toISOString(),
      journeyId,
      profile: trustedProfile,
      parameters: normalizeAttributionParameters(search),
      region: shopifyRegion,
    }, now);
  };

  const chooseLastAdTouch = (touches, journeyId, now = Date.now()) => {
    const normalizedJourneyId = sanitizeToken(journeyId, 36, JOURNEY_ID_PATTERN);
    if (!Array.isArray(touches) || !normalizedJourneyId) return null;
    // Distinct visits through the SAME ad are distinct touches. Only timestamp
    // and touch ID determine recency; ad identity is never a deduplication key.
    return touches.reduce((latest, candidate) => {
      const touch = normalizeAdTouch(candidate, now);
      if (!touch || touch.journeyId !== normalizedJourneyId) return latest;
      if (!latest || touch.observedAt > latest.observedAt ||
        (touch.observedAt === latest.observedAt && touch.touchId > latest.touchId) ||
        (touch.touchId === latest.touchId && touch.observedAt === latest.observedAt &&
          latest.reconstructed && !touch.reconstructed)) return touch;
      return latest;
    }, null);
  };

  const adTouchFromCartAttributes = (attributes, journeyId, now = Date.now()) => {
    if (!attributes || typeof attributes !== 'object' || attributes.sm_touch_schema !== '1') return null;
    const normalizedJourneyId = sanitizeToken(journeyId, 36, JOURNEY_ID_PATTERN);
    if (!normalizedJourneyId || attributes.sm_journey_id !== normalizedJourneyId) return null;
    const parameters = {};
    Object.values(PARAMETER_SPEC).forEach((spec) => {
      const key = spec.attribute.replace(/^sm_/, 'sm_last_');
      if (!TOUCH_ATTRIBUTE_KEYS.includes(key)) return;
      const value = sanitizeParameter(attributes[key], spec);
      if (value) parameters[spec.attribute] = value;
    });
    const location = normalizeShopifyRegion(attributes.sm_last_shopify_region);
    const region = location.country === attributes.sm_last_shopify_country ? location.region : '';
    return normalizeAdTouch({
      schema: AD_TOUCH_SCHEMA_VERSION,
      reconstructed: true,
      touchId: attributes.sm_last_touch_id,
      observedAt: attributes.sm_last_touch_at,
      journeyId: normalizedJourneyId,
      profile: {
        kind: 'landing',
        profileKey: attributes.sm_last_landing_key,
        pageVersion: attributes.sm_last_landing_version,
        path: attributes.sm_last_landing_path,
      },
      parameters,
      region,
    }, now);
  };

  const adTouchAttributes = (touch, now = Date.now()) => {
    const attributes = Object.fromEntries(TOUCH_ATTRIBUTE_KEYS.map((key) => [key, '']));
    const normalized = normalizeAdTouch(touch, now);
    if (!normalized) return attributes;
    const location = normalizeShopifyRegion(normalized.region);
    Object.assign(attributes, {
      sm_touch_schema: String(AD_TOUCH_SCHEMA_VERSION),
      sm_last_touch_id: normalized.touchId,
      sm_last_touch_at: normalized.observedAt,
      sm_last_landing_key: normalized.profile.profileKey,
      sm_last_landing_version: normalized.profile.pageVersion,
      sm_last_landing_path: normalized.profile.path,
      sm_last_shopify_region: location.region,
      sm_last_shopify_country: location.country,
    });
    Object.entries(normalized.parameters).forEach(([key, value]) => {
      const lastKey = key.replace(/^sm_/, 'sm_last_');
      if (TOUCH_ATTRIBUTE_KEYS.includes(lastKey)) attributes[lastKey] = value;
    });
    return attributes;
  };

  const normalizeJourney = (journey = {}) => {
    const normalized = {};
    const selectedFormat = sanitizeToken(journey.selectedFormat, 20, /^[a-z]+$/);
    const upsellKey = sanitizeToken(journey.upsellKey, 80, PROFILE_KEY_PATTERN);
    const upsellVersion = sanitizeToken(journey.upsellVersion, 180, VERSION_PATTERN);
    const upsellDecision = sanitizeToken(journey.upsellDecision, 20, /^[a-z]+$/);

    if (ALLOWED_FORMATS.includes(selectedFormat)) normalized.sm_selected_format = selectedFormat;
    if (upsellKey) normalized.sm_upsell_key = upsellKey;
    if (upsellVersion) normalized.sm_upsell_version = upsellVersion;
    if (ALLOWED_DECISIONS.includes(upsellDecision)) normalized.sm_upsell_decision = upsellDecision;

    return normalized;
  };

  const mergeCartAttributes = (existingAttributes, record, journey = {}) => {
    const normalizedRecord = normalizeFirstTouchRecord(record);
    if (!normalizedRecord) return null;

    const merged = {};
    if (existingAttributes && typeof existingAttributes === 'object') {
      Object.entries(existingAttributes).forEach(([key, value]) => {
        if (typeof key === 'string' && typeof value === 'string') merged[key] = value;
      });
    }

    ORIGIN_ATTRIBUTE_KEYS.forEach((key) => {
      merged[key] = normalizedRecord.attributes[key] || '';
    });

    const normalizedJourney = normalizeJourney(journey);
    if (Object.keys(normalizedJourney).length) {
      JOURNEY_ATTRIBUTE_KEYS.forEach((key) => {
        merged[key] = '';
      });
      Object.assign(merged, normalizedJourney);
    }

    return merged;
  };

  const clearOwnedAttributes = (existingAttributes) => {
    const cleared = {};

    if (existingAttributes && typeof existingAttributes === 'object') {
      Object.entries(existingAttributes).forEach(([key, value]) => {
        if (typeof key === 'string' && typeof value === 'string') cleared[key] = value;
      });
    }

    OWNED_ATTRIBUTE_KEYS.forEach((key) => {
      cleared[key] = '';
    });

    return cleared;
  };

  const normalizeAction = (dataset, trustedProfile) => {
    const profile = normalizeTrustedProfile(trustedProfile);
    if (!dataset || !profile) return null;

    const type = sanitizeToken(dataset.smAction, 24, /^[a-z-]+$/);
    const selectedFormat = sanitizeToken(dataset.smFormat, 20, /^[a-z]+$/);
    const journey = { selectedFormat };
    const ctaId = sanitizeToken(dataset.smCtaId, 80, CTA_TOKEN_PATTERN);
    const ctaPlacement = sanitizeToken(dataset.smCtaPlacement, 80, CTA_TOKEN_PATTERN);
    let destination = '';

    if (!ALLOWED_FORMATS.includes(selectedFormat)) return null;

    if (profile.kind === 'upsell') {
      const decision = sanitizeToken(dataset.smUpsellDecision, 20, /^[a-z]+$/);
      if (!ALLOWED_DECISIONS.includes(decision)) return null;
      journey.upsellKey = profile.profileKey;
      journey.upsellVersion = profile.pageVersion;
      journey.upsellDecision = decision;
    }

    if (type === 'direct-cart') {
      const variantId = sanitizeToken(dataset.smVariantId, 24, VARIANT_ID_PATTERN);
      const discountCode = sanitizeToken(dataset.smDiscountCode, 80, DISCOUNT_PATTERN);
      if (!variantId || !discountCode) return null;
      destination = { type, variantId, discountCode };
    } else if (type === 'upsell') {
      const path = sanitizeToken(dataset.smDestination, 180, PAGE_PATH_PATTERN);
      if (!path) return null;
      destination = { type, path };
    } else if (type === 'external') {
      const candidate = sanitizeText(dataset.smDestination, 500);
      let url;
      try {
        url = new URL(candidate);
      } catch (_error) {
        return null;
      }
      if (url.protocol !== 'https:') return null;
      destination = { type, url: url.toString() };
    } else {
      return null;
    }

    return Object.freeze({
      type,
      journey: Object.freeze(journey),
      destination: Object.freeze(destination),
      cta: Object.freeze({ id: ctaId, placement: ctaPlacement }),
    });
  };

  const FUNNEL_EVENT_NAMES = Object.freeze({
    adTouchObserved: 'starmist:ad_touch_observed',
    landingViewed: 'starmist:landing_viewed',
    landingChapterStarted: 'starmist:landing_chapter_started',
    landingOfferClicked: 'starmist:landing_offer_clicked',
    upsellViewed: 'starmist:upsell_viewed',
    upsellDecision: 'starmist:upsell_decision',
  });

  const originEventFields = (record, currentShopifyRegion = '', now = Date.now()) => {
    const normalizedRecord = normalizeFirstTouchRecord(record, now);
    const attributes = normalizedRecord?.attributes || {};
    const currentLocation = normalizeShopifyRegion(currentShopifyRegion);
    const storedLocation = normalizeShopifyRegion(attributes.sm_shopify_region);
    const location = currentLocation.region ? currentLocation : storedLocation;

    return Object.freeze({
      journeyId: attributes.sm_journey_id || '',
      firstLandingKey: attributes.sm_first_landing_key || '',
      firstLandingVersion: attributes.sm_first_landing_version || '',
      firstLandingPath: attributes.sm_first_landing_path || '',
      firstLandingAt: attributes.sm_first_landing_at || '',
      utmSource: attributes.sm_utm_source || '',
      utmMedium: attributes.sm_utm_medium || '',
      utmCampaign: attributes.sm_utm_campaign || '',
      utmContent: attributes.sm_utm_content || '',
      utmTerm: attributes.sm_utm_term || '',
      utmId: attributes.sm_utm_id || '',
      metaCampaignId: attributes.sm_meta_campaign_id || '',
      metaCampaignName: attributes.sm_meta_campaign_name || '',
      metaAdsetId: attributes.sm_meta_adset_id || '',
      metaAdsetName: attributes.sm_meta_adset_name || '',
      metaAdId: attributes.sm_meta_ad_id || '',
      metaAdName: attributes.sm_meta_ad_name || '',
      fbclid: attributes.sm_fbclid || '',
      shopifyRegion: location.region,
      shopifyCountry: location.country,
    });
  };

  const baseFunnelEvent = (trustedProfile, record, shopifyRegion = '', now = Date.now()) => {
    const profile = normalizeTrustedProfile(trustedProfile);
    if (!profile) return null;

    return {
      schema: EVENT_SCHEMA_VERSION,
      profileKind: profile.kind,
      profileKey: profile.profileKey,
      profileRevision: profile.profileRevision,
      profileVersion: profile.pageVersion,
      templateRelease: profile.templateRelease,
      path: profile.path,
      attributionLane: profile.attributionLane,
      themeRole: profile.themeRole,
      ...(profile.surface === 'product'
        ? { surface: 'product', productId: profile.productId, productTitle: profile.productTitle }
        : {}),
      ...originEventFields(record, shopifyRegion, now),
    };
  };

  const buildAdTouchEvent = (touch, firstTouchRecord, now = Date.now()) => {
    const normalized = normalizeAdTouch(touch, now);
    if (!normalized || normalized.reconstructed) return null;
    const record = normalizeFirstTouchRecord(firstTouchRecord, now);
    if (!record || record.attributes.sm_journey_id !== normalized.journeyId) return null;
    const base = baseFunnelEvent(normalized.profile, record, normalized.region, now);
    if (!base) return null;
    const parameters = normalized.parameters;
    return Object.freeze({
      ...base,
      eventKind: 'ad_touch_observed',
      touchSchema: AD_TOUCH_SCHEMA_VERSION,
      touchId: normalized.touchId,
      touchAt: normalized.observedAt,
      touchUtmSource: parameters.sm_utm_source || '',
      touchUtmMedium: parameters.sm_utm_medium || '',
      touchUtmCampaign: parameters.sm_utm_campaign || '',
      touchUtmContent: parameters.sm_utm_content || '',
      touchUtmTerm: parameters.sm_utm_term || '',
      touchUtmId: parameters.sm_utm_id || '',
      touchMetaCampaignId: parameters.sm_meta_campaign_id || '',
      touchMetaCampaignName: parameters.sm_meta_campaign_name || '',
      touchMetaAdsetId: parameters.sm_meta_adset_id || '',
      touchMetaAdsetName: parameters.sm_meta_adset_name || '',
      touchMetaAdId: parameters.sm_meta_ad_id || '',
      touchMetaAdName: parameters.sm_meta_ad_name || '',
      touchFbclid: parameters.sm_fbclid || '',
    });
  };

  const buildFunnelViewEvent = (trustedProfile, record, shopifyRegion = '') => {
    const profile = normalizeTrustedProfile(trustedProfile);
    const base = baseFunnelEvent(profile, record, shopifyRegion);
    if (!profile || !base) return null;

    return Object.freeze({
      ...base,
      eventKind: profile.kind === 'landing' ? 'landing_viewed' : 'upsell_viewed',
    });
  };

  const buildLandingChapterStartEvent = (trustedProfile, cta, record, shopifyRegion = '') => {
    const profile = normalizeTrustedProfile(trustedProfile);
    const base = baseFunnelEvent(profile, record, shopifyRegion);
    if (!profile || profile.kind !== 'landing' || !base) return null;

    const ctaId = sanitizeToken(cta?.id, 80, CTA_TOKEN_PATTERN);
    const ctaPlacement = sanitizeToken(cta?.placement, 80, CTA_TOKEN_PATTERN);
    if (!ctaId || !ctaPlacement) return null;

    return Object.freeze({
      ...base,
      eventKind: 'landing_chapter_started',
      ctaId,
      ctaPlacement,
    });
  };

  const buildOfferClickEvent = (trustedProfile, action, record, shopifyRegion = '') => {
    const profile = normalizeTrustedProfile(trustedProfile);
    const base = baseFunnelEvent(profile, record, shopifyRegion);
    if (!profile || !base || !action || !action.journey) return null;

    const selectedFormat = sanitizeToken(action.journey.selectedFormat, 20, /^[a-z]+$/);
    if (!ALLOWED_FORMATS.includes(selectedFormat)) return null;

    const upsellDecision = sanitizeToken(action.journey.upsellDecision, 20, /^[a-z]+$/);
    const variantId = sanitizeToken(action.variantId, 24, VARIANT_ID_PATTERN);
    const purchaseMethod = ['add-to-cart', 'accelerated-checkout'].includes(action.purchaseMethod)
      ? action.purchaseMethod : '';
    if (profile.surface === 'product' &&
      action.type !== 'direct-cart' && !(action.type === 'external' && selectedFormat === 'audio')) return null;

    return Object.freeze({
      ...base,
      eventKind: profile.kind === 'landing' ? 'landing_offer_clicked' : 'upsell_decision',
      format: selectedFormat,
      audience: selectedFormat === 'audio' ? 'listener' : 'reader',
      action: sanitizeToken(action.type, 24, /^[a-z-]+$/),
      ctaId: sanitizeToken(action.cta?.id, 80, CTA_TOKEN_PATTERN),
      ctaPlacement: sanitizeToken(action.cta?.placement, 80, CTA_TOKEN_PATTERN),
      upsellDecision: ALLOWED_DECISIONS.includes(upsellDecision) ? upsellDecision : '',
      ...(profile.surface === 'product' && variantId ? { variantId } : {}),
      ...(profile.surface === 'product' && action.type === 'direct-cart' && purchaseMethod ? { purchaseMethod } : {}),
    });
  };

  const productFormat = (options) => {
    if (!Array.isArray(options)) return '';
    for (const option of options) {
      const label = typeof option === 'string' ? option.trim().toLowerCase() : '';
      if (/\b(hardcover|hardback)\b/.test(label)) return 'hardcover';
      if (/\bpaperback\b/.test(label)) return 'paperback';
      if (/\b(e-?book)\b/.test(label)) return 'ebook';
      if (/\b(audio|audiobook)\b/.test(label)) return 'audio';
    }
    return '';
  };

  const funnelViewEventName = (trustedProfile) => {
    const profile = normalizeTrustedProfile(trustedProfile);
    if (!profile) return '';
    return profile.kind === 'landing' ? FUNNEL_EVENT_NAMES.landingViewed : FUNNEL_EVENT_NAMES.upsellViewed;
  };

  const offerClickEventName = (trustedProfile) => {
    const profile = normalizeTrustedProfile(trustedProfile);
    if (!profile) return '';
    return profile.kind === 'landing'
      ? FUNNEL_EVENT_NAMES.landingOfferClicked
      : FUNNEL_EVENT_NAMES.upsellDecision;
  };

  const buildDestination = (action, routesRoot = '/', cartAttributes = null) => {
    if (!action || !action.destination) return '';

    if (action.destination.type === 'direct-cart') {
      const root = typeof routesRoot === 'string' && routesRoot.startsWith('/') ? routesRoot : '/';
      const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
      const query = new URLSearchParams({
        discount: action.destination.discountCode,
        storefront: 'true',
      });

      // Shopify cart permalinks accept cart attributes as query parameters.
      // Carry only contract-owned keys so the browser can begin navigation
      // immediately without waiting on an Ajax cart read/write round trip.
      if (cartAttributes && typeof cartAttributes === 'object') {
        OWNED_ATTRIBUTE_KEYS.forEach((key) => {
          if (typeof cartAttributes[key] === 'string') {
            query.append(`attributes[${key}]`, cartAttributes[key]);
          }
        });
      }

      return `${normalizedRoot}cart/${action.destination.variantId}:1?${query.toString()}`;
    }

    if (action.destination.type === 'upsell') return action.destination.path;
    if (action.destination.type === 'external') return action.destination.url;
    return '';
  };

  return Object.freeze({
    SCHEMA_VERSION,
    EVENT_SCHEMA_VERSION,
    AD_TOUCH_SCHEMA_VERSION,
    ATTRIBUTION_WINDOW_MS,
    ORIGIN_ATTRIBUTE_KEYS,
    JOURNEY_ATTRIBUTE_KEYS,
    TOUCH_ATTRIBUTE_KEYS,
    OWNED_ATTRIBUTE_KEYS,
    PARAMETER_SPEC,
    sanitizeText,
    normalizeShopifyRegion,
    normalizeAttributionParameters,
    normalizeTrustedProfile,
    createFirstTouchRecord,
    normalizeFirstTouchRecord,
    ensureFirstTouchJourneyId,
    firstTouchFromCartAttributes,
    chooseFirstTouch,
    createAdTouch,
    normalizeAdTouch,
    chooseLastAdTouch,
    adTouchFromCartAttributes,
    adTouchAttributes,
    buildAdTouchEvent,
    normalizeJourney,
    mergeCartAttributes,
    clearOwnedAttributes,
    normalizeAction,
    FUNNEL_EVENT_NAMES,
    buildFunnelViewEvent,
    buildLandingChapterStartEvent,
    buildOfferClickEvent,
    productFormat,
    funnelViewEventName,
    offerClickEventName,
    buildDestination,
  });
});
